import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, ArrowRight, Phone, MessageSquare, Send, Database, Mail } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame } from "./_shared";
import { getCatalog } from "@/lib/api";

// Static fallback mirrors the catalog's group:"revenue" packages.
const FALLBACK = [
  { name: "AI Lead Starter", priceFrom: "from $99/mo", tagline: "For small local businesses", bullets: ["Website chat assistant", "Smart forms", "Instant email/SMS response", "Basic qualification", "Appointment booking", "Lead inbox", "Simple analytics", "Human notifications"] },
  { name: "AI Sales Growth", priceFrom: "from $299/mo", tagline: "For established businesses", popular: true, bullets: ["Everything in Starter", "AI voice receptionist (Ava)", "CRM integration", "Multichannel nurturing (Relay)", "Lead scoring", "Missed-call recovery", "No-show prevention", "Review workflows", "Campaign attribution", "Closed-lead revival"] },
  { name: "AI Revenue Workforce", priceFrom: "Custom", tagline: "For larger orgs & agencies", bullets: ["Prospect discovery & enrichment (Scout)", "Intent-signal monitoring", "Advanced outbound campaigns", "Multiple AI agents", "Multiple brands & locations", "Bidirectional CRM sync", "Custom qualification logic", "Account expansion", "Revenue forecasting", "Role-based access & approval", "Compliance & audit center", "White-label client dashboards"] },
];

const USAGE = [
  { icon: Phone, t: "Voice minutes" }, { icon: MessageSquare, t: "SMS" }, { icon: Send, t: "WhatsApp" },
  { icon: Database, t: "Data enrichment" }, { icon: Mail, t: "Email volume" },
];

export default function RevenuePackages() {
  const [tiers, setTiers] = useState(FALLBACK);
  useEffect(() => {
    window.scrollTo(0, 0); document.title = "Revenue Center Packages | WebsiteTech.pro";
    getCatalog().then((d) => {
      const rev = (d.packages || []).filter((p) => p.group === "revenue");
      if (rev.length) setTiers(rev);
    }).catch(() => {});
  }, []);

  return (
    <RevShell testid="rev-packages-page">
      <RevHero eyebrow="AI Revenue Center™ · Packages"
        title="Start small." accent="Grow into a full revenue workforce."
        sub="Three levels that scale with you — from a local-business starter to multi-agent, multi-brand operations. Usage-based charges for voice, SMS, WhatsApp, enrichment and email are billed separately and transparently."
        visual={
          <BrowserFrame url="app.websitetech.pro/packages">
            <div className="space-y-2">
              {FALLBACK.map((t) => (
                <div key={t.name} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
                  <span className="text-sm font-semibold text-slate-700">{t.name}</span>
                  <span className="text-xs font-bold text-brand">{t.priceFrom}</span>
                </div>
              ))}
            </div>
          </BrowserFrame>
        } />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="rev-tiers">
        <div className="grid lg:grid-cols-3 gap-5 items-start">
          {tiers.map((t, i) => (
            <motion.div key={t.name} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              data-testid={`rev-tier-${i}`}
              className={`rounded-3xl border bg-white p-7 relative ${t.popular ? "border-brand card-shadow ring-1 ring-brand/20 lg:-translate-y-2" : "border-slate-200 card-shadow"}`}>
              {t.popular && <span className="absolute -top-3 left-7 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide bg-brand text-white rounded-full px-3 py-1"><Star size={11} /> Most popular</span>}
              <h3 className="font-heading text-xl font-extrabold text-slate-900">{t.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{t.tagline}</p>
              <div className="text-2xl font-bold text-brand mt-4">{t.priceFrom}</div>
              <ul className="mt-5 space-y-2.5">
                {(t.bullets || []).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-600"><Check size={15} className="text-brand mt-0.5 shrink-0" /> {b}</li>
                ))}
              </ul>
              <Link to="/audit" data-testid={`rev-tier-cta-${i}`}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 font-semibold rounded-xl px-5 py-3 transition-transform hover:-translate-y-0.5 ${t.popular ? "bg-brand text-white" : "btn-outline"}`}>
                Get started <ArrowRight size={15} />
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6 text-center">Indicative pricing. Final quotes depend on volume, integrations and support level.</p>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Transparent usage" title="Pay only for what you use" sub="These consumption items are metered separately from your plan — never bundled into surprise fees." />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-8">
            {USAGE.map((u) => (
              <div key={u.t} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center">
                <div className="h-10 w-10 rounded-xl bg-white text-brand grid place-items-center mx-auto card-shadow"><u.icon size={18} /></div>
                <div className="text-sm font-semibold text-slate-700 mt-3">{u.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RevCTA title="Not sure which level fits?" sub="Start with a free audit. We'll recommend the right tier based on your traffic, team and goals — no pressure." />
    </RevShell>
  );
}
